import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GoHeartFill } from "react-icons/go";
import { BiSolidMessageSquareDetail } from "react-icons/bi";
import { IoBookmark } from "react-icons/io5";
import { FaShare } from 'react-icons/fa';
import { doc, updateDoc, arrayUnion, arrayRemove, increment, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { FaUser } from 'react-icons/fa';
import CommentSection from './CommentSection';
import Modal from 'react-modal';
import VerificationBadge from '../components/VerificationBadge';

Modal.setAppElement('#root');

export default function ListingItem({ listing }) {
    const auth = getAuth();
    const navigate = useNavigate();
    const [isLiked, setIsLiked] = useState(listing.likedBy?.includes(auth.currentUser?.uid) || false);
    const [isSaved, setIsSaved] = useState(listing.savedBy?.includes(auth.currentUser?.uid) || false);
    const [likeCount, setLikeCount] = useState(listing.likes || 0);
    const [imageError, setImageError] = useState(false);
    const [likeAnimation, setLikeAnimation] = useState(false);
    const [saveAnimation, setSaveAnimation] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [userData, setUserData] = useState({
        name: '',
        photoURL: '',
        verified: false
    });

    // Fetch user data for the listing owner
    useEffect(() => {
        if (!listing.userRef) return;

        const userRef = doc(db, 'users', listing.userRef);
        const unsubscribe = onSnapshot(userRef, (doc) => {
            if (doc.exists()) {
                const data = doc.data();
                setUserData({
                    name: data.name || 'Unknown User',
                    photoURL: data.photoURL || '',
                    verified: data.verified || false
                });
            }
        });

        return () => unsubscribe();
    }, [listing.userRef]);

    // Format price with commas
    const formatPrice = (price) => {
        if (price === 'Negotiable') return price;
        return Number(price).toLocaleString();
    };

    const handleLike = async (e) => {
        e.stopPropagation();
        if (!auth.currentUser) {
            toast.error("Please sign in to like listings");
             navigate('/welcome');
            return;
        }

        const listingRef = doc(db, 'listings', listing.id);
        const userId = auth.currentUser.uid;

        try {
            setLikeAnimation(true);
            setTimeout(() => setLikeAnimation(false), 1000);
            
            if (isLiked) {
                await updateDoc(listingRef, {
                    likedBy: arrayRemove(userId),
                    likes: likeCount - 1
                });
                setLikeCount(likeCount - 1);
                setIsLiked(false);
            } else {
                await updateDoc(listingRef, {
                    likedBy: arrayUnion(userId),
                    likes: likeCount + 1
                });
                setLikeCount(likeCount + 1);
                setIsLiked(true);
            }
        } catch (error) {
            toast.error("Error updating like status");
            console.error("Like error:", error);
        }
    };

    const handleSave = async (e) => {
        e.stopPropagation();
        if (!auth.currentUser) {
            toast.error("Please sign in to save listings");
             navigate('/welcome');
            return;
        }

        const listingRef = doc(db, 'listings', listing.id);
        const userId = auth.currentUser.uid;

        try {
            setSaveAnimation(true);
            setTimeout(() => setSaveAnimation(false), 1000);
            
            if (isSaved) {
                await updateDoc(listingRef, {
                    savedBy: arrayRemove(userId)
                });
                setIsSaved(false);
            } else {
                await updateDoc(listingRef, {
                    savedBy: arrayUnion(userId)
                });
                setIsSaved(true);
            }
        } catch (error) {
            toast.error("Error updating save status");
            console.error("Save error:", error);
        }
    };

    const handleComment = (e) => {
        e.stopPropagation();
        if (!auth.currentUser) {
            toast.error("Please sign in to comment");
             navigate('/welcome');
            return;
        }
        setShowComments(!showComments);
    };

    const handleShare = async (e) => {
        e.stopPropagation();
        
        const shareUrl = `${window.location.origin}/listing/${listing.id}`;
        const shareData = {
            title: listing.title,
            text: `Check out "${listing.title}" on AweitApp!`,
            url: shareUrl,
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(shareUrl);
                toast.info('Listing link copied to clipboard! Share it manually.');
            }
        } catch (error) {
            console.error('Error sharing listing:', error);
            try {
                await navigator.clipboard.writeText(shareUrl);
                toast.info('Listing link copied to clipboard! Share it manually.');
            } catch (clipboardError) {
                console.error('Error copying to clipboard:', clipboardError);
                toast.error('Failed to share listing. Please share the link manually.');
            }
        }
    };

    const handleListingClick = async () => {
        // Allow anyone to view the listing, regardless of auth status
        try {
            // Only update view count if user is authenticated and not the owner
            if (auth.currentUser && auth.currentUser.uid !== listing.userRef) {
                const listingRef = doc(db, 'listings', listing.id);
                await updateDoc(listingRef, {
                    viewCount: increment(1)
                });
            } else if (!auth.currentUser) {
                // For non-authenticated users, we could optionally track views in a different way
                // For now, just don't update the count
                console.log('Guest viewing listing - view count not updated');
            }
        } catch (error) {
            console.error('Error updating view count:', error);
            // Don't show error to user as it's not critical
        }

        // Navigate to the listing page (works for all users)
        navigate(`/listing/${listing.id}`);
    };

    const handleImageError = () => {
        setImageError(true);
    };

    if (!listing.id) {
        throw new Error("ListingItem requires a listing object with an 'id' property");
    }

    return (
        <div className="relative group">
            <style jsx>{`
                .price-badge {
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    background: #FF0000;
                    color: white;
                    font-size: 10px;
                    font-weight: bold;
                    padding: 2px 8px;
                    border-radius: 9999px;
                    z-index: 10;
                    text-transform: uppercase;
                    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
                }

                @keyframes likeBounce {
                    0%, 100% { transform: scale(1); }
                    25% { transform: scale(1.2); }
                    50% { transform: scale(0.9); }
                    75% { transform: scale(1.1); }
                }

                @keyframes saveBounce {
                    0%, 100% { transform: scale(1); }
                    25% { transform: scale(1.2) rotate(-10deg); }
                    50% { transform: scale(0.9); }
                    75% { transform: scale(1.1) rotate(10deg); }
                }

                .shelf-container {
                    perspective: 1000px;
                }
                .shelf-item {
                    position: relative;
                    transform-style: preserve-3d;
                    transition: all 0.3s ease;
                }
                .shelf-item::before {
                    content: '';
                    position: absolute;
                    bottom: -6px;
                    left: 0;
                    width: 100%;
                    height: 6px;
                    background: linear-gradient(to bottom, #d1d1d1, #a3a3a3);
                    transform: rotateX(-45deg);
                    transform-origin: top;
                    border-radius: 0 0 3px 3px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                .shelf-item::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 50%);
                    border-radius: 6px;
                    pointer-events: none;
                }
                .shelf-item:hover {
                    transform: translateY(-4px) rotateX(5deg);
                }
                .shelf-item-inner {
                    position: relative;
                    z-index: 2;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    border-radius: 6px;
                    overflow: hidden;
                    box-shadow: 0 3px 10px rgba(0,0,0,0.1);
                    border: 1px solid rgba(255,255,255,0.2);
                    background: linear-gradient(135deg, #1a1a1a, #2d2d2d);
                }
            `}</style>

            <div className="shelf-container">
                <div className="shelf-item h-full">
                    <div className="shelf-item-inner">
                        {/* Price Badge */}
                        <div className="price-badge">
                            {listing.price === 'Negotiable' ? 'Negotiable' : `${listing.currency} ${formatPrice(listing.price)}`}
                        </div>

                        <div className="w-full h-full min-h-[180px] rounded-md overflow-hidden group flex flex-col cursor-pointer">
                            {/* Listing Image */}
                            <div 
                                className="flex-1 relative overflow-hidden"
                                onClick={handleListingClick}
                            >
                                {imageError ? (
                                    <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                                        <span className="text-gray-900"></span>
                                    </div>
                                ) : (
                                    <img 
                                        src={listing.imageUrls?.[0] || ''} 
                                        alt={listing.title}
                                        className="w-full h-full object-cover group-hover:opacity-75 transition-opacity duration-200"
                                        loading="lazy"
                                        decoding="async"
                                        onError={handleImageError}
                                    />
                                )}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                                    <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                        View Listing
                                    </span>
                                </div>
                            </div>

                            {/* Listing Details */}
                            <div className="p-2 flex flex-col items-center text-white">
                                {/* Title */}
                                <h3 
                                    className="text-xs sm:text-[11px] uppercase truncate w-full text-center font-medium"
                                    onClick={handleListingClick}
                                >
                                    {listing.title}
                                </h3>
                                
                                {/* Owner and Category */}
                                <div 
                                    className="flex items-center space-x-2 mt-1"
                                    onClick={handleListingClick}
                                >
                                    {userData.photoURL ? (
                                        <img
                                            src={userData.photoURL}
                                            alt={userData.name}
                                            className="w-6 h-6 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center">
                                            <span className="text-gray-900"></span>
                                        </div>
                                    )}
                                    <div className="flex flex-col">
                                        <div className="flex items-center">
                                            <p className="text-[10px] sm:text-[9px] text-gray-400 truncate">
                                                {userData.name}
                                            </p>
                                            <VerificationBadge isVerified={userData.verified} />
                                        </div>
                                        <p className="text-[10px] sm:text-[9px] text-pink-400 truncate">
                                            {listing.category}
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Actions */}
                                <div 
                                    className="w-full bg-gray-800 mt-2 py-1 flex justify-around items-center rounded"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <button 
                                        onClick={handleLike}
                                        className="flex items-center hover:text-[#FF1493] transition-colors duration-200"
                                    >
                                        <GoHeartFill 
                                            size={17} 
                                            className={`${isLiked ? "text-[#FF1493]" : "text-white"} ${likeAnimation ? "animate-[likeBounce_1s_ease-in-out]" : ""}`} 
                                        />
                                    </button>
                                    <button 
                                        onClick={handleComment}
                                        className="flex items-center hover:text-gray-300 transition-colors duration-200"
                                    >
                                        <BiSolidMessageSquareDetail size={17} className={`text-white ${showComments ? 'text-pink-500' : ''}`} />
                                    </button>
                                    <button 
                                        onClick={handleSave}
                                        className="flex items-center hover:text-[#1fa9ff] transition-colors duration-200"
                                    >
                                        <IoBookmark 
                                            size={17} 
                                            className={`${isSaved ? "text-[#1fa9ff]" : "text-white"} ${saveAnimation ? "animate-[saveBounce_1s_ease-in-out]" : ""}`} 
                                        />
                                    </button>
                                    <button 
                                        onClick={handleShare}
                                        className="flex items-center hover:text-gray-300 transition-colors duration-200"
                                    >
                                        <FaShare size={17} className="text-white" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Comment Section in Modal */}
            <Modal
                isOpen={showComments}
                onRequestClose={() => setShowComments(false)}
                style={{
                    content: {
                        top: '50%',
                        left: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: '#1a1a1a',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '20px',
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        width: '90%',
                        maxWidth: '500px',
                    },
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                        zIndex: 1000,
                    },
                }}
                contentLabel="Comment Modal"
            >
                <CommentSection contentId={listing.id} contentType="listing" />
                <button
                    onClick={() => setShowComments(false)}
                    className="mt-4 px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600"
                >
                    Close
                </button>
            </Modal>
        </div>
    );
}